import { retentionService } from '../../services/retention.service';
import notify from '../../util/notification';

const sortByCreatedAtDesc = (list) =>
  [...list].sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0));

// Apply a duplicate/shorten result: upsert created drafts, the (possibly new)
// group doc, and the source's re-stamped groupId (null on rollback).
const applyGroupingResult = (commit, result) => {
  (result.created || []).forEach((draft) => commit('UPSERT_MESSAGE', draft));
  if (result.group) commit('UPSERT_GROUP', result.group);
  if (result.source && result.source.id) {
    commit('UPSERT_MESSAGE', { id: result.source.id, groupId: result.source.groupId ?? null });
  }
};

const state = {
  messages: [],
  groups: [],
  images: [],
  defaults: [],
  productIds: [],
  productIdsAppId: null,
  realtimeProducts: [],
  realtimeProductsAppId: null,
  configurationStatus: {},
  loading: {
    messages: false,
    groups: false,
    images: false,
    defaults: false
  }
};

const mutations = {
  // --- Messages ---
  SET_MESSAGES(state, payload) {
    state.messages = sortByCreatedAtDesc(Array.isArray(payload) ? payload : []);
  },
  UPSERT_MESSAGE(state, message) {
    if (!message || !message.id) return;
    const idx = state.messages.findIndex(m => m.id === message.id);
    if (idx === -1) {
      state.messages = [message, ...state.messages];
    } else {
      state.messages.splice(idx, 1, { ...state.messages[idx], ...message });
    }
  },
  REMOVE_MESSAGE(state, id) {
    state.messages = state.messages.filter(m => m.id !== id);
  },

  // --- Groups ---
  SET_GROUPS(state, payload) {
    state.groups = Array.isArray(payload) ? payload : [];
  },
  UPSERT_GROUP(state, group) {
    if (!group || !group.groupId) return;
    const idx = state.groups.findIndex(g => g.groupId === group.groupId);
    if (idx === -1) {
      state.groups = [group, ...state.groups];
    } else {
      state.groups.splice(idx, 1, { ...state.groups[idx], ...group });
    }
  },
  REMOVE_GROUP(state, groupId) {
    state.groups = state.groups.filter(g => g.groupId !== groupId);
  },

  // --- Images ---
  SET_IMAGES(state, payload) {
    state.images = sortByCreatedAtDesc(Array.isArray(payload) ? payload : []);
  },
  UPSERT_IMAGE(state, image) {
    if (!image || !image.id) return;
    const idx = state.images.findIndex(i => i.id === image.id);
    if (idx === -1) {
      state.images = [image, ...state.images];
    } else {
      state.images.splice(idx, 1, { ...state.images[idx], ...image });
    }
  },
  REMOVE_IMAGE(state, id) {
    state.images = state.images.filter(i => i.id !== id);
  },

  // --- Defaults ---
  SET_DEFAULTS(state, payload) {
    state.defaults = Array.isArray(payload) ? payload : [];
  },
  UPSERT_DEFAULT(state, def) {
    if (!def || !def.id) return;
    const idx = state.defaults.findIndex(d => d.id === def.id);
    if (idx === -1) {
      state.defaults = [def, ...state.defaults];
    } else {
      state.defaults.splice(idx, 1, { ...state.defaults[idx], ...def });
    }
  },
  REMOVE_DEFAULT(state, id) {
    state.defaults = state.defaults.filter(d => d.id !== id);
  },

  // --- Catalog products ---
  SET_PRODUCT_IDS(state, { appId, products }) {
    state.productIds = Array.isArray(products) ? products : [];
    state.productIdsAppId = appId;
  },
  CLEAR_PRODUCT_IDS(state) {
    state.productIds = [];
    state.productIdsAppId = null;
  },
  SET_REALTIME_PRODUCTS(state, { appId, products }) {
    state.realtimeProducts = Array.isArray(products) ? products : [];
    state.realtimeProductsAppId = appId;
  },

  // --- Loading flags ---
  SET_LOADING(state, { key, value }) {
    state.loading[key] = value;
  },

  // --- Retention configuration status (per-app readiness for the warning banner) ---
  SET_CONFIG_STATUS(state, { appId, status }) {
    state.configurationStatus = { ...state.configurationStatus, [appId]: status };
  }
};

const actions = {
  // --- Messages ---
  async fetchMessages({ commit }) {
    try {
      commit('SET_LOADING', { key: 'messages', value: true });
      const response = await retentionService.getAllMessages();
      commit('SET_MESSAGES', response.data?.result || []);
    } catch (error) {
      console.error('Error fetching retention messages:', error);
      commit('SET_MESSAGES', []);
    } finally {
      commit('SET_LOADING', { key: 'messages', value: false });
    }
  },
  async addMessage({ commit }, payload) {
    try {
      const res = await retentionService.addMessage(payload);
      if (res.status === 200) {
        commit('UPSERT_MESSAGE', res.data.result);
        notify('Retention message draft created.', 'success');
        return res;
      }
      notify("Retention message couldn't be created.", 'error');
      throw res;
    } catch (err) {
      notify(err.response?.data?.message || "Retention message couldn't be created.", 'error');
      console.error(err);
      throw err;
    }
  },
  async editMessage({ commit }, { id, data }) {
    try {
      const res = await retentionService.editMessage(id, data);
      if (res.status === 200) {
        commit('UPSERT_MESSAGE', res.data.result);
        notify('Retention message updated.', 'success');
        return res;
      }
      notify("Retention message couldn't be updated.", 'error');
      throw res;
    } catch (err) {
      notify(err.response?.data?.message || "Retention message couldn't be updated.", 'error');
      console.error(err);
      throw err;
    }
  },
  async submitMessage({ commit }, payload) {
    const id = typeof payload === 'string' ? payload : payload.id;
    const silent = typeof payload === 'object' && payload.silent;
    try {
      const res = await retentionService.submitMessage(id);
      if (res.status === 200) {
        commit('UPSERT_MESSAGE', res.data.result);
        if (!silent) notify('Submitted to Apple for review.', 'success');
        return res;
      }
      if (!silent) notify("Couldn't submit to Apple.", 'error');
      throw res;
    } catch (err) {
      if (!silent) notify(err.response?.data?.message || "Couldn't submit to Apple.", 'error');
      console.error(err);
      throw err;
    }
  },
  async duplicateMessageToLanguages({ commit }, { messageId, targetLanguages }) {
    try {
      const res = await retentionService.duplicateMessageToLanguages(messageId, targetLanguages);
      const result = res.data?.result || { created: [], failures: [], skipped: [] };
      applyGroupingResult(commit, result);
      return result;
    } catch (err) {
      notify(err.response?.data?.message || "Couldn't duplicate to other languages.", 'error');
      console.error(err);
      throw err;
    }
  },
  async shortenSkippedTranslations({ commit }, { messageId, items }) {
    try {
      const res = await retentionService.shortenSkippedTranslations(messageId, items);
      const result = res.data?.result || { created: [], failures: [], skipped: [] };
      applyGroupingResult(commit, result);
      return result;
    } catch (err) {
      notify(err.response?.data?.message || "Couldn't shorten translations.", 'error');
      console.error(err);
      throw err;
    }
  },
  async deleteMessage({ commit }, payload) {
    const id = typeof payload === 'string' ? payload : payload.id;
    const silent = typeof payload === 'object' && payload.silent;
    try {
      const res = await retentionService.deleteMessage(id);
      if (res.status === 200) {
        const r = res.data?.result || {};
        commit('REMOVE_MESSAGE', r.deletedId || id);
        // Dissolved group: drop its doc and revert the lone survivor to standalone.
        if (r.dissolvedGroupId) commit('REMOVE_GROUP', r.dissolvedGroupId);
        if (r.clearedMessageId) commit('UPSERT_MESSAGE', { id: r.clearedMessageId, groupId: null });
        if (!silent) notify('Retention message deleted.', 'success');
        return res;
      }
      if (!silent) notify("Retention message couldn't be deleted.", 'error');
      throw res;
    } catch (err) {
      const data = err.response?.data;
      if (!silent) {
        if (data?.blockingDefaults?.length) {
          const pairs = data.blockingDefaults.map(d => `${d.productId} / ${d.locale}`).join(', ');
          notify(
            `Cannot delete: this message is set as default for ${pairs}. Remove the default(s) first.`,
            'error'
          );
        } else {
          notify(data?.message || "Retention message couldn't be deleted.", 'error');
        }
      }
      console.error(err);
      throw err;
    }
  },

  // --- Groups ---
  async fetchGroups({ commit }) {
    try {
      commit('SET_LOADING', { key: 'groups', value: true });
      const res = await retentionService.getAllGroups();
      commit('SET_GROUPS', res.data?.result || []);
    } catch (err) {
      console.error('Error fetching retention message groups:', err);
      commit('SET_GROUPS', []);
    } finally {
      commit('SET_LOADING', { key: 'groups', value: false });
    }
  },
  async addMessageToGroup({ commit }, { messageId, targetMessageId }) {
    try {
      const res = await retentionService.addMessageToGroup({ messageId, targetMessageId });
      const r = res.data?.result || {};
      if (r.updatedSource) commit('UPSERT_MESSAGE', r.updatedSource);
      if (r.updatedTarget) commit('UPSERT_MESSAGE', r.updatedTarget);
      if (r.newGroupDoc) commit('UPSERT_GROUP', r.newGroupDoc);
      notify('Message added to the group.', 'success');
      return r;
    } catch (err) {
      notify(err.response?.data?.message || "Couldn't add the message to the group.", 'error');
      console.error(err);
      throw err;
    }
  },
  async deleteGroup({ commit, dispatch }, { groupId }) {
    try {
      const res = await retentionService.deleteGroup(groupId);
      if (res.status === 200) {
        const ids = res.data?.result?.deletedIds || [];
        ids.forEach((id) => commit('REMOVE_MESSAGE', id));
        commit('REMOVE_GROUP', groupId);
        notify('Group and all its messages deleted.', 'success');
        return res;
      }
      throw res;
    } catch (err) {
      const data = err.response?.data;
      if (data?.blockers?.length) {
        // Blockers precheck refused; nothing was deleted server-side.
        const refs = data.blockers
          .map((b) => `${b.name || b.messageId}`)
          .join(', ');
        notify(
          `Cannot delete the group: ${refs} ${data.blockers.length > 1 ? 'are' : 'is'} set as a default. Remove the default(s) first.`,
          'error'
        );
      } else {
        // Partial cascade: drop what the server reports deleted, then resync.
        (data?.deletedIds || []).forEach((id) => commit('REMOVE_MESSAGE', id));
        notify(data?.message || "Group couldn't be deleted.", 'error');
        dispatch('fetchMessages');
        dispatch('fetchGroups');
      }
      console.error(err);
      throw err;
    }
  },

  // --- Images ---
  async fetchImages({ commit }) {
    try {
      commit('SET_LOADING', { key: 'images', value: true });
      const response = await retentionService.getAllImages();
      commit('SET_IMAGES', response.data?.result || []);
    } catch (error) {
      console.error('Error fetching retention images:', error);
      commit('SET_IMAGES', []);
    } finally {
      commit('SET_LOADING', { key: 'images', value: false });
    }
  },
  async addImage({ commit }, payload) {
    try {
      // Backwards-tolerant: accept either a raw base64 string or
      // { image, name, description, usage }.
      const args =
        typeof payload === 'string'
          ? { image: payload }
          : {
              image: payload.image,
              name: payload.name,
              description: payload.description,
              usage: payload.usage
            };
      const res = await retentionService.addImage(args);
      if (res.status === 200) {
        commit('UPSERT_IMAGE', res.data.result);
        notify('Image draft created.', 'success');
        return res.data.result;
      }
      throw res;
    } catch (err) {
      notify(err.response?.data?.message || "Image couldn't be created.", 'error');
      console.error(err);
      throw err;
    }
  },
  async editImage({ commit }, { id, data }) {
    try {
      const res = await retentionService.editImage(id, data);
      if (res.status === 200) {
        commit('UPSERT_IMAGE', res.data.result);
        notify('Image updated.', 'success');
        return res.data.result;
      }
      throw res;
    } catch (err) {
      notify(err.response?.data?.message || "Image couldn't be updated.", 'error');
      console.error(err);
      throw err;
    }
  },
  async submitImage({ commit }, payload) {
    const id = typeof payload === 'string' ? payload : payload.id;
    const silent = typeof payload === 'object' && payload.silent;
    try {
      const res = await retentionService.submitImage(id);
      if (res.status === 200) {
        commit('UPSERT_IMAGE', res.data.result);
        if (!silent) notify('Image submitted to Apple for review.', 'success');
        return res.data.result;
      }
      throw res;
    } catch (err) {
      if (!silent) notify(err.response?.data?.message || "Image couldn't be submitted.", 'error');
      console.error(err);
      throw err;
    }
  },
  async deleteImage({ commit }, payload) {
    const id = typeof payload === 'string' ? payload : payload.id;
    const silent = typeof payload === 'object' && payload.silent;
    try {
      const res = await retentionService.deleteImage(id);
      if (res.status === 200) {
        commit('REMOVE_IMAGE', id);
        if (!silent) notify('Image deleted.', 'success');
        return res;
      }
      throw res;
    } catch (err) {
      const data = err.response?.data;
      if (!silent) {
        if (data?.blockingMessages?.length) {
          const refs = data.blockingMessages
            .map(m => `${m.name || m.header || m.messageId} (${m.messageState})`)
            .join(', ');
          notify(
            `Cannot delete: this image is referenced by ${refs}. Delete those messages first.`,
            'error'
          );
        } else {
          notify(data?.message || "Image couldn't be deleted.", 'error');
        }
      }
      console.error(err);
      throw err;
    }
  },

  // --- Defaults ---
  async fetchDefaults({ commit }) {
    try {
      commit('SET_LOADING', { key: 'defaults', value: true });
      const res = await retentionService.getAllDefaults();
      commit('SET_DEFAULTS', res.data?.result || []);
    } catch (err) {
      console.error('Error fetching retention defaults:', err);
      commit('SET_DEFAULTS', []);
    } finally {
      commit('SET_LOADING', { key: 'defaults', value: false });
    }
  },
  async fetchProducts({ commit, state, rootGetters }, { force = false } = {}) {
    const activeApp = rootGetters['app/activeApp'];
    if (!activeApp) return;
    if (!force && state.productIdsAppId === activeApp && state.productIds.length > 0) {
      return state.productIds;
    }
    try {
      const res = await retentionService.getProducts();
      const products = res.data?.result || [];
      commit('SET_PRODUCT_IDS', { appId: activeApp, products });
      return products;
    } catch (err) {
      console.error('Error fetching catalog products:', err);
      commit('SET_PRODUCT_IDS', { appId: activeApp, products: [] });
      throw err;
    }
  },
  // Realtime builder products come from the manually maintained legacy catalog
  // (offers carry price/source there), unlike fetchProducts which serves the
  // store-synced mobile catalog for the default-message screens.
  async fetchRealtimeProducts({ commit, state, rootGetters }, { force = false } = {}) {
    const activeApp = rootGetters['app/activeApp'];
    if (!activeApp) return;
    if (!force && state.realtimeProductsAppId === activeApp && state.realtimeProducts.length > 0) {
      return state.realtimeProducts;
    }
    try {
      const res = await retentionService.getRealtimeProducts();
      const products = res.data?.result || [];
      commit('SET_REALTIME_PRODUCTS', { appId: activeApp, products });
      return products;
    } catch (err) {
      console.error('Error fetching realtime catalog products:', err);
      commit('SET_REALTIME_PRODUCTS', { appId: activeApp, products: [] });
      throw err;
    }
  },
  async configureDefault({ commit }, { productId, locale, messageIdentifier, silent = false }) {
    try {
      const res = await retentionService.configureDefault({ productId, locale, messageIdentifier });
      if (res.status === 200) {
        commit('UPSERT_DEFAULT', res.data.result);
        if (!silent) notify('Default message configured.', 'success');
        return res.data.result;
      }
      throw res;
    } catch (err) {
      if (!silent) notify(err.response?.data?.message || "Default couldn't be configured.", 'error');
      console.error(err);
      throw err;
    }
  },
  async removeDefault({ commit }, { productId, locale, silent = false }) {
    try {
      const res = await retentionService.removeDefault({ productId, locale });
      if (res.status === 200) {
        commit('REMOVE_DEFAULT', `${productId}__${locale}`);
        if (!silent) notify('Default removed (the message itself was not deleted).', 'success');
        return res;
      }
      throw res;
    } catch (err) {
      if (!silent) notify(err.response?.data?.message || "Default couldn't be removed.", 'error');
      console.error(err);
      throw err;
    }
  },
  invalidateProductIdsCache({ commit }) {
    commit('CLEAR_PRODUCT_IDS');
  },

  // --- Retention configuration ---
  async fetchConfigurationStatus({ commit }, appId) {
    if (!appId) return null;
    try {
      const res = await retentionService.getRetentionConfigStatus(appId);
      const status = res.data?.result || { ready: false, missing: [], env: null };
      commit('SET_CONFIG_STATUS', { appId, status });
      return status;
    } catch (err) {
      console.error('Error fetching retention configuration status:', err);
      const fallback = { ready: false, missing: [], env: null, error: true };
      commit('SET_CONFIG_STATUS', { appId, status: fallback });
      return fallback;
    }
  }
};

const getters = {
  messages: state => state.messages,
  messageById: state => id => state.messages.find(m => m.id === id),
  approvedMessages: state =>
    state.messages.filter(m => m.messageState === 'APPROVED'),

  images: state => state.images,
  imageById: state => id => state.images.find(i => i.id === id),
  approvedImages: state =>
    state.images.filter(i => i.imageState === 'APPROVED'),

  // All retention messages are Apple-reviewed text-based types (text / image /
  // bulleted / bulleted_image). Default-eligible == APPROVED — no type filter
  // needed.
  defaultEligibleMessages: state =>
    state.messages.filter((m) => m.messageState === 'APPROVED'),

  // --- Groups ---
  groups: state => state.groups,
  groupsById: state => {
    const map = {};
    for (const g of state.groups) map[g.groupId] = g;
    return map;
  },
  // groupId -> members (all states; callers filter to APPROVED as needed).
  messagesByGroupId: state => {
    const map = {};
    for (const m of state.messages) {
      if (!m.groupId) continue;
      (map[m.groupId] = map[m.groupId] || []).push(m);
    }
    return map;
  },

  defaults: state => state.defaults,
  productIds: state => state.productIds,
  productIdsAreFreshFor: state => appId => state.productIdsAppId === appId,
  realtimeProducts: state => state.realtimeProducts,

  isLoadingMessages: state => state.loading.messages,
  isLoadingGroups: state => state.loading.groups,
  isLoadingImages: state => state.loading.images,
  isLoadingDefaults: state => state.loading.defaults,

  configurationStatus: state => state.configurationStatus,
  configurationStatusFor: state => appId => state.configurationStatus[appId] || null,
  // True only when we have an explicit ready=true from the backend. Null/loading and
  // ready=false both gate UI off — safer default than optimistic enable.
  isConfiguredFor: state => appId => !!(state.configurationStatus[appId] && state.configurationStatus[appId].ready === true)
};

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters
};
